import { TransactionOrderType } from "creso-core/constants";
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
  Button,
  V2Loader,
  FormCurrencyInput,
  Badge,
  cn,
} from "web-design-system";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import omit from "lodash/omit";
import { useRouter } from "@tanstack/react-router";
import {
  useUpdateOneTimeTransaction,
  useScheme,
  useProposal,
  useSchemes,
  useCreateOneTimeTransactionV2,
  useInvalidateTransactionOrder,
  useLastUsedFolios,
} from "creso-core/hooks";
import { useFieldArray, useForm } from "react-hook-form";
import { NotepadText, ShoppingBasketIcon, X } from "lucide-react";
import { isNewFolio, isPurchaseOrder, NEW_FOLIO_VALUE } from "creso-core/utils";
import { toast } from "sonner";
import { getFormattedAmount } from "@funds/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { purchaseFormValidationSchema } from "creso-core/zod";
import { z } from "zod";
import { APIError } from "web-core/utils";
import { isEmpty } from "lodash";
import { FolioNumberDropdown } from "web-core";
import { TransactionContext } from "./transaction-wizard";
import { SchemesSearchDropdown } from "./schemes-search-dropdown";
import FormActions from "./form-actions";
import { handlePurchaseFormValidationError } from "../utils/error";
import { BasketPicker } from "./import-collection";
import { useCollectionGroups } from "../hooks/use-collection-groups";

type DefaultValueType = z.infer<typeof purchaseFormValidationSchema>;

const DEFAULT_VALUE: DefaultValueType = {
  schemes: [
    {
      scheme: null,
      folio_id: null,
      amount: "",
      collection: null,
    },
  ],
};

export default function OneTimeForm() {
  const router = useRouter();
  const {
    selectedOrder,
    updateContext,
    collectionId,
    collectionType,
    collectionTarget,
  } = useContext(TransactionContext);

  const { investment_account_guid = "" } = useContext(TransactionContext);

  const { data: scheme, isLoading: isSchemesLoading } = useScheme(
    selectedOrder?.type === TransactionOrderType.PURCHASE
      ? selectedOrder?.scheme_id
      : 0,
    "all"
  );

  const createOneTimeTransaction = useCreateOneTimeTransactionV2();

  const invalidateDraftOrders = useInvalidateTransactionOrder(
    investment_account_guid
  );

  const updateOneTimeTransaction = useUpdateOneTimeTransaction({
    investment_account_guid,
  });

  const { data: foliosWithLastUsedInfo } = useLastUsedFolios({
    investment_account_guid,
  });

  const { data: proposal, isLoading: isProposalLoading } = useProposal(
    { proposalId: collectionId as number },
    {
      enabled:
        !!collectionId &&
        collectionType === "proposal" &&
        collectionTarget?.includes(TransactionOrderType.PURCHASE),
    }
  );

  const proposalIsins = useMemo(
    () =>
      proposal?.proposal_funds
        .filter((f) => f.type === "LUMPSUM")
        .map((f) => f.fund_scheme_isin),
    [proposal]
  );

  const { schemes: schemesFromProposals, isLoading: isLoadingProposalSchemes } =
    useSchemes<"all">({
      type: "all",
      isin: proposalIsins?.join(","),
      enabled: !!proposalIsins?.length,
    });

  const formValues = useMemo(() => {
    if (isPurchaseOrder(selectedOrder)) {
      return {
        schemes: [
          {
            scheme,
            folio_id: selectedOrder.folio_id ?? null,
            amount: selectedOrder.amount ?? "",
          },
        ],
      };
    }
    if (!collectionTarget?.includes(TransactionOrderType.PURCHASE))
      return DEFAULT_VALUE;
    if (schemesFromProposals) {
      return {
        ...DEFAULT_VALUE,
        schemes: schemesFromProposals?.map((s) => {
          const pFund = proposal?.proposal_funds.find(
            (f) => f.fund_scheme_isin === s.scheme_isin && f.type === "LUMPSUM"
          );
          const defaultFolio = foliosWithLastUsedInfo
            ?.filter((f) => f.internal_flag)
            .find((f) => f.amc_id === s.amc_id);
          return {
            scheme: s,
            amount: Number(pFund?.lumpsum_amount ?? 0).toFixed(0),
            folio_id: pFund?.source_folio ?? defaultFolio?.id ?? null,
            collection: {
              name: proposal?.name ?? "",
              id: proposal?.id ?? 0,
              type: "proposal" as const,
            },
          };
        }),
      };
    }
    return DEFAULT_VALUE;
  }, [
    schemesFromProposals,
    proposal,
    collectionTarget,
    selectedOrder,
    scheme,
    foliosWithLastUsedInfo,
  ]);

  const form = useForm({
    defaultValues: DEFAULT_VALUE,
    resolver: zodResolver(purchaseFormValidationSchema),
  });

  const handleUpdateOrder = useCallback(
    async (values: DefaultValueType) => {
      if (!selectedOrder?.id || !values.schemes[0].scheme) return {};
      try {
        const result = await updateOneTimeTransaction.mutateAsync({
          id: selectedOrder.id,
          scheme_isin: values.schemes[0].scheme?.scheme_isin,
          folio_id: isNewFolio(values.schemes[0].folio_id)
            ? null
            : values.schemes[0].folio_id,
          amount: values.schemes[0].amount,
          investment_account_guid,
        });
        return { data: result, error: null };
      } catch (error) {
        return {
          data: null,
          error,
          isHandled: handlePurchaseFormValidationError(error, form, 0),
        };
      }
    },
    [form, selectedOrder, updateOneTimeTransaction, investment_account_guid]
  );

  const handleAddOrders = useCallback(
    async (values: DefaultValueType) => {
      const results = await Promise.allSettled(
        values.schemes.map((item) => {
          return createOneTimeTransaction.mutateAsync({
            scheme_isin: item.scheme?.scheme_isin as string,
            folio_id: isNewFolio(item.folio_id) ? null : item.folio_id,
            amount: item.amount,
            investment_account_guid,
          });
        })
      );

      await invalidateDraftOrders.invalidate();

      return results.map((r, idx) => {
        if (r.status === "fulfilled") {
          return { data: r.value, error: null };
        }
        return {
          data: null,
          error: r.reason,
          isHandled: handlePurchaseFormValidationError(r.reason, form, idx),
        };
      });
    },
    [
      form,
      createOneTimeTransaction,
      investment_account_guid,
      invalidateDraftOrders,
    ]
  );

  const onSubmit = useCallback(
    async (values: DefaultValueType) => {
      if (selectedOrder?.id) {
        const result = await handleUpdateOrder(values);
        if (result.error) {
          if (result.isHandled === false) {
            toast.error(`Failed to update order details`, {
              description: (result.error as APIError)?.data?.message,
            });
          }
          return;
        }
      } else {
        const results = await handleAddOrders(values);
        let hasErrors = false;
        const erroredSchemes: DefaultValueType["schemes"] = [];
        results.forEach((r, idx) => {
          if (r.error) {
            hasErrors = true;
            if (r.isHandled === false) {
              toast.error(`Failed to save order details`, {
                description: (r.error as APIError)?.data?.message,
              });
            }
            erroredSchemes.push(values.schemes[idx]);
          }
        });
        if (hasErrors) {
          form.setValue("schemes", erroredSchemes);
          return;
        }
      }

      form.reset(DEFAULT_VALUE);

      if (collectionType !== "proposal") {
        updateContext?.({
          selectedOrder: null,
          collectionId: undefined,
          collectionType: undefined,
        });
      } else {
        const newCollectionTarget = collectionTarget?.filter(
          (t) => t !== TransactionOrderType.PURCHASE
        );
        updateContext?.({
          selectedOrder: null,
          collectionId: isEmpty(newCollectionTarget) ? undefined : collectionId,
          collectionType: isEmpty(newCollectionTarget)
            ? undefined
            : collectionType,
          collectionTarget: newCollectionTarget,
        });
        if (newCollectionTarget?.length) {
          router.navigate({
            to: ".",
            search: (prev) => ({ ...prev, type: newCollectionTarget[0] }),
            replace: true,
          });
        }
      }
    },
    [
      selectedOrder,
      updateContext,
      form,
      handleAddOrders,
      handleUpdateOrder,
      collectionId,
      collectionTarget,
      collectionType,
      router,
    ]
  );

  const {
    fields: schemeFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    name: "schemes",
    control: form.control,
  });

  const {
    collectionGroups,
    otherRows: otherSchemes,
    customCollectionKeys,
    handleRemoveCollection,
    handleRemoveScheme,
    handleAmountChange,
    recordOriginalAmounts,
    clearCustomKey,
    resetCollectionState,
  } = useCollectionGroups<DefaultValueType["schemes"][number] & { id: string }>(
    {
      rows: schemeFields,
      remove,
      getCollection: (r) => r.collection,
      getSchemeKey: (r) => r.scheme?.scheme_isin ?? null,
      getAmount: (r) => r.amount,
    }
  );

  const [editingCollectionId, setEditingCollectionId] = useState<number | null>(
    collectionType === "basket" ? (collectionId ?? null) : null
  );

  const resetForm = useCallback(() => {
    resetCollectionState();
    form.reset(formValues);
  }, [form, resetCollectionState, formValues]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const renderFundRow = (
    f: (typeof schemeFields)[number],
    idx: number,
    collectionKey?: string
  ) => {
    return (
      <div key={f.id} className="grid grid-cols-7 w-full gap-2">
        <FormField
          className="col-span-3"
          control={form.control}
          name={`schemes.${idx}.scheme`}
          render={({ field }) => (
            <FormItem>
              <SchemesSearchDropdown
                value={
                  field.value
                    ? {
                        label: field.value.scheme_name,
                        value: field.value.scheme_isin,
                      }
                    : null
                }
                onChange={(val) => {
                  field.onChange(val);
                  const defaultFolio = foliosWithLastUsedInfo
                    ?.filter((f) => f.internal_flag)
                    .find((f) => f.amc_id === val?.amc_id);
                  if (defaultFolio) {
                    form.setValue(`schemes.${idx}.folio_id`, defaultFolio.id);
                  }
                }}
                placeholder="Select Scheme"
                type="all"
                disabled={!!collectionKey}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          className="col-span-2"
          control={form.control}
          name={`schemes.${idx}.folio_id`}
          render={({ field }) => {
            return (
              <FormItem>
                <FolioNumberDropdown
                  key={form.watch(`schemes.${idx}.scheme`)?.amc_id}
                  amc_id={form.watch(`schemes.${idx}.scheme`)?.amc_id}
                  value={field.value?.toString() ?? NEW_FOLIO_VALUE}
                  onChange={(value) => {
                    if (value === NEW_FOLIO_VALUE) {
                      field.onChange(null);
                    } else {
                      field.onChange(value);
                    }
                    if (form.getFieldState(`schemes.${idx}.amount`).isDirty)
                      form.trigger(`schemes.${idx}.amount`);
                  }}
                  filters={[(f) => f.internal_flag]}
                  investmentAccountGuid={investment_account_guid}
                />
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <div className="col-span-2 flex items-start gap-2">
          <FormField
            control={form.control}
            name={`schemes.${idx}.amount`}
            render={({ field }) => {
              const scheme = form.watch(`schemes.${idx}.scheme`);
              const folioId = form.watch(`schemes.${idx}.folio_id`);
              const minAmount = folioId
                ? scheme?.additional_purchase_min_amount || 0
                : scheme?.new_purchase_min_amount || 0;
              return (
                <FormCurrencyInput
                  description={
                    scheme?.product_type !== "SIF"
                      ? `Min ${getFormattedAmount(minAmount)}`
                      : ""
                  }
                  {...field}
                  placeholder="₹ Amount"
                  onChange={(e) => {
                    field.onChange(e);
                    if (collectionKey) {
                      handleAmountChange(collectionKey, e as string, idx);
                    }
                  }}
                />
              );
            }}
          />
          {schemeFields.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={() => handleRemoveScheme(idx, collectionKey)}
            >
              <X />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-1">
      <h5 className="mt-1">
        {selectedOrder?.id ? "Update" : "Create"} One time order
      </h5>
      <div className="h-px bg-border/50 w-full mt-3 mb-4" />
      <V2Loader
        isLoading={
          isSchemesLoading || isProposalLoading || isLoadingProposalSchemes
        }
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 flex-1 px-1 flex flex-col items-start"
          >
            {!collectionGroups.length || editingCollectionId != null ? (
              <BasketPicker<"all">
                schemeType="all"
                basketId={editingCollectionId}
                onCancel={() => {
                  setEditingCollectionId(null);
                  if (collectionType === "proposal") {
                    router.navigate({
                      to: ".",
                      search: (prev) =>
                        omit(prev as Record<string, unknown>, [
                          "collection_id",
                          "collection_type",
                        ]),
                      replace: true,
                    });
                  }
                }}
                onImport={async (results) => {
                  form.clearErrors();
                  const currentSchemes = form.getValues("schemes");
                  const importedId = results[0].collection.id;
                  const collectionKey = `collection_${importedId}`;
                  const filtered = currentSchemes.filter((s) => {
                    if (!s.collection) return true;
                    return (
                      `collection_${s.collection.id ?? s.collection.name}` !==
                      collectionKey
                    );
                  });
                  const internalLastUsedFolios = foliosWithLastUsedInfo?.filter(
                    (f) => f.internal_flag
                  );
                  replace([
                    ...results.map((r) => {
                      const defaultFolio = internalLastUsedFolios?.find(
                        (f) => f.amc_id === r.scheme.amc_id
                      );
                      return {
                        scheme: r.scheme,
                        folio_id: defaultFolio?.id ?? null,
                        amount: r.amount,
                        collection: {
                          name: r.collection.name,
                          id: r.collection.id,
                          type: r.collection.type,
                        },
                      };
                    }),
                    ...filtered.filter(
                      (f) => f.scheme || f.folio_id || f.amount
                    ),
                  ]);
                  clearCustomKey(collectionKey);
                  recordOriginalAmounts(
                    importedId,
                    results.map((r) => ({
                      key: r.scheme.scheme_isin,
                      amount: r.amount,
                    }))
                  );
                  setEditingCollectionId(null);
                }}
              />
            ) : null}
            {collectionGroups
              .filter(({ collection }) => collection.id !== editingCollectionId)
              .map(({ key, schemes, collection }) => (
                <div
                  key={key}
                  className={cn(
                    "flex flex-col gap-4 border border-border rounded-lg px-4 py-3 w-full",
                    "bg-linear-to-r from-tag-yellow to-background",
                    collection.type === "proposal" &&
                      "from-tag-green to-background"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {collection.type === "basket" ? (
                      <ShoppingBasketIcon className="size-5 text-tag-yellow-foreground" />
                    ) : (
                      <NotepadText className="size-5 text-tag-green-foreground" />
                    )}
                    <span className="text-sm font-semibold">
                      {collection.name}
                    </span>
                    {customCollectionKeys.has(key) && (
                      <Badge
                        variant="custom"
                        className="text-tag-slate-foreground bg-tag-slate"
                      >
                        Custom
                      </Badge>
                    )}
                    <div className="ml-auto flex items-center gap-6">
                      {!customCollectionKeys.has(key) &&
                        collection.id != null &&
                        collection.type === "basket" && (
                          <Button
                            type="button"
                            variant="link"
                            className="text-xs h-auto p-0 font-medium hover:underline"
                            onClick={() =>
                              setEditingCollectionId(collection.id!)
                            }
                          >
                            Edit Total Amount
                          </Button>
                        )}
                      <Button
                        type="button"
                        variant="link"
                        className="text-xs h-auto p-0 font-medium text-destructive hover:underline"
                        onClick={() => {
                          handleRemoveCollection(key, schemes);
                          if (collection.type === "proposal") {
                            updateContext?.({
                              collectionTarget: collectionTarget?.filter(
                                (t) => t !== TransactionOrderType.PURCHASE
                              ),
                            });
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    {schemes.map((f) => renderFundRow(f, f.idx, key))}
                  </div>
                </div>
              ))}
            <div className={cn("space-y-4", collectionGroups.length && "px-4")}>
              {otherSchemes.map((f) => renderFundRow(f, f.idx))}
            </div>
            {!selectedOrder?.id && (
              <Button
                className={cn("-mt-4", collectionGroups.length && "ml-2")}
                onClick={() =>
                  append({
                    scheme: null,
                    folio_id: null,
                    amount: "",
                  })
                }
                type="button"
                variant="ghost"
              >
                + Add another Scheme
              </Button>
            )}
            <FormActions
              dirty={form.formState.isDirty}
              onReset={resetForm}
              isUpdate={!!selectedOrder?.id}
            />
          </form>
        </Form>
      </V2Loader>
    </div>
  );
}
